import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { injectIntl } from 'react-intl';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { Divider, Hidden, Typography, Checkbox } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { isEmpty } from 'lodash';
import messages from 'aurora-translations';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { numberMaskWithoutDecimalOption } from 'aurora-utils/constants';
import { fetchUserDetails } from 'aurora-composition/modules/common/actions';
import { ADGMTextField, ADGMButton } from 'aurora-components';
import { DocumentsV2 } from 'aurora-section-components';
import ADGMPanelInputsGeneratorBody from 'aurora-components/ADGMPanelInputsGenerator/ADGMPanelInputsGeneratorBody';

import { REPRESENTATIVE_TYPES, SOURCE_OF_AUTHORIZATIONS, REPRESENTATIVE_REQUIRED_KEYS as KEYS } from '../../constants';
import { transformDetails } from '../helpers';

import styles from './styles';

function AddNewRepresentative({
  intl,
  classes,
  locale,
  isEditFlow,
  application,
  representative,
  addRepresentativeForAll,
  addForAllPartiesText,
  documents,
  docsAttributes,
  setRepresentative,
  changeRepresentativeDetailsHandler,
  uploadDocumentHandler,
  deleteDocumentHandler,
  changeRepresentativeForAllHandler,
}) {
  const [userEID, setUserEID] = useState(representative?.eid ?? null);
  const [fetchUserError, setFetchUserError] = useState(null);
  const dispatch = useDispatch();

  const handleEIDChange = e => {
    if (fetchUserError) {
      setFetchUserError(null);
    }
    setUserEID(e.target.value);
  };

  const fetchRepresentativeDetails = () => {
    dispatch(fetchUserDetails(userEID?.replace(/-/g, ''))).then(res => {
      if (res?.success) {
        setRepresentative(transformDetails(res?.result));
      } else {
        setFetchUserError(res?.message);
      }
    });
  };

  const BUYER_DETAILS_SECTIONS = useMemo(() => {
    if (isEmpty(representative)) return null;
    return [
      {
        fields: [
          {
            type: 'text',
            label: intl.formatMessage({ ...messages.Name_English_bracket }),
            placeholder: intl.formatMessage({ ...messages.Name_English_hint }),
            value: representative?.nameEn,
            required: true,
            disabled: true,
          },
          {
            type: 'text',
            label: intl.formatMessage({ ...messages.Name_Arabic_bracket }),
            placeholder: intl.formatMessage({ ...messages.Name_Arabic_hint }),
            value: representative?.nameAr,
            required: true,
            disabled: true,
          },
          {
            type: 'text',
            label: intl.formatMessage({ ...messages.nationality }),
            placeholder: intl.formatMessage({ ...messages.nationality }),
            value: locale === 'en' ? representative?.nationalityEn : representative?.nationalityAr,
            required: true,
            disabled: true,
          },
          {
            type: 'text',
            label: intl.formatMessage({ ...messages.phone_number }),
            placeholder: intl.formatMessage({ ...messages.phone_number }),
            value: representative?.[KEYS?.phoneNo],
            mask: createNumberMask(numberMaskWithoutDecimalOption),
            onChange: e => changeRepresentativeDetailsHandler(KEYS?.phoneNo, e?.target?.value),
            required: true,
          },
          {
            type: 'text',
            label: intl.formatMessage({ ...messages.Email }),
            placeholder: intl.formatMessage({ ...messages.Email }),
            value: representative?.[KEYS?.email],
            onChange: e => changeRepresentativeDetailsHandler(KEYS?.email, e?.target?.value),
            required: true,
          },
          {
            type: 'dropdown',
            label: intl.formatMessage({ ...messages.representative_type }),
            placeholder: intl.formatMessage({ ...messages.representative_type }),
            value: representative?.[KEYS?.representativeType],
            options: REPRESENTATIVE_TYPES,
            onChange: (_, data) => changeRepresentativeDetailsHandler(KEYS?.representativeType, data?.value),
            required: true,
          },
          {
            type: 'dropdown',
            label: intl.formatMessage({ ...messages.poa_source_of_authorization }),
            placeholder: intl.formatMessage({ ...messages.poa_source_of_authorization }),
            value: representative?.[KEYS?.sourceOfAuthorization],
            options: SOURCE_OF_AUTHORIZATIONS,
            onChange: (_, data) => changeRepresentativeDetailsHandler(KEYS?.sourceOfAuthorization, data?.value),
            required: true,
          },
          {
            type: 'text',
            label: intl.formatMessage({ ...messages.authorization_no }),
            placeholder: intl.formatMessage({ ...messages.authorization_no }),
            value: representative?.[KEYS?.authorizationNo] ?? '',
            onChange: e => changeRepresentativeDetailsHandler(KEYS?.authorizationNo, e?.target?.value),
            required: true,
          },
          {
            type: 'date',
            date: representative?.[KEYS?.authorizationDate]
              ? moment(representative?.[KEYS?.authorizationDate]).format('YYYY-MM-DD')
              : '',
            onChange: date =>
              changeRepresentativeDetailsHandler(KEYS?.authorizationDate, moment(date).format('YYYY-MM-DD')),
            dateComponentProps: {
              label: intl.formatMessage({
                ...messages.authorization_date,
              }),
              placeholder: `${intl.formatMessage({ ...messages.select })} ${intl.formatMessage({
                ...messages.authorization_date,
              })}`,
              value: representative?.[KEYS?.authorizationDate]
                ? moment(representative?.[KEYS?.authorizationDate]).format('DD/MM/YYYY')
                : '',
              autoComplete: 'off',
              handleInputChange: date =>
                date && changeRepresentativeDetailsHandler(KEYS?.authorizationDate, moment(date).format('YYYY-MM-DD')),
              required: true,
            },
          },
        ],
      },
    ];
  }, [intl, representative]);

  return (
    <>
      <div className={classes.fetchUserDetailsHolder}>
        <ADGMTextField
          variant='outlined'
          type='text'
          label={keyEmptyCheck('eid_un_number')}
          placeholder='000-0000-000000-0'
          value={userEID}
          onChange={handleEIDChange}
          required
          disabled={isEditFlow}
          customClasses={{ container: classes.fetchDetailsInputContainer }}
          {...(fetchUserError && { error: keyEmptyCheck(fetchUserError) })}
          {...(!fetchUserError && { helperText: keyEmptyCheck('Enter_UN_EID_number_fetch_person_details') })}
          mask={[
            /\d/,
            /\d/,
            /\d/,
            '-',
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            '-',
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            /\d/,
            '-',
            /\d/,
          ]}
        />
        <ADGMButton
          className={classes.fetchDetailsBtn}
          onClick={fetchRepresentativeDetails}
          disabled={isEditFlow || isEmpty(userEID)}
        >
          {keyEmptyCheck('fetch')}
        </ADGMButton>
      </div>

      {!isEmpty(BUYER_DETAILS_SECTIONS) && (
        <div className={classes.details}>
          <ADGMPanelInputsGeneratorBody
            isEditable
            sections={BUYER_DETAILS_SECTIONS}
            bodyStyle={classes.detailsContent}
          />
        </div>
      )}

      {!isEmpty(docsAttributes) && !isEmpty(representative) && (
        <>
          <Hidden xsDown>
            <Divider className={classes.divider} />
          </Hidden>
          <DocumentsV2
            title='representative_documents'
            isPanel={false}
            isEditable
            handleUploadDoc={uploadDocumentHandler}
            handleDeleteDoc={deleteDocumentHandler}
            hideDownloadAllBtn
            {...{
              locale,
              applicationId: application?.applicationId,
              applicationType: application?.applicationDetails?.applicationType,
              documents,
              application,
              attributes: docsAttributes,
            }}
          />
        </>
      )}

      {!isEmpty(representative) && addForAllPartiesText && (
        <div className={classes.addForAllHolder}>
          <Checkbox
            edge='start'
            checked={addRepresentativeForAll}
            onChange={e => changeRepresentativeForAllHandler(e.target.checked)}
            disableRipple
            classes={{
              root: classes.addForAllCheckbox,
              checked: classes.addForAllChecked,
            }}
          />
          <Typography className={classes.addForAllText}>{keyEmptyCheck(addForAllPartiesText)}</Typography>
        </div>
      )}
    </>
  );
}

AddNewRepresentative.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  isEditFlow: PropTypes.bool.isRequired,
  application: PropTypes.object.isRequired,
  representative: PropTypes.object.isRequired,
  addRepresentativeForAll: PropTypes.bool.isRequired,
  addForAllPartiesText: PropTypes.string.isRequired,
  documents: PropTypes.array.isRequired,
  docsAttributes: PropTypes.array.isRequired,
  setRepresentative: PropTypes.func.isRequired,
  changeRepresentativeDetailsHandler: PropTypes.func.isRequired,
  uploadDocumentHandler: PropTypes.func.isRequired,
  deleteDocumentHandler: PropTypes.func.isRequired,
  changeRepresentativeForAllHandler: PropTypes.func.isRequired,
};

export default withStyles(styles)(injectIntl(AddNewRepresentative));
